/**
 * The plan set's manufacturer documents in the Permit workspace: which datasheets the set appends after E-4 (and
 * which are missing, the `documents_datasheets` problem), the rapid-shutdown device for a string inverter without
 * module-level electronics (Equipment step), and the attachment with its fastener and embedment (Structure step,
 * drawn on sheet PV-4).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, ExternalLink, Eye, FileCheck2, FileUp, FileWarning, Trash2 } from "lucide-react";
import { useRef } from "react";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrgId } from "../api/org";
import { Badge, Button, Card, Field, NumberInput, Select, TextInput, useToast } from "../components/ui";
import { canAttachOwn, datasheetState, missingCount, ownDocumentId, pdfProblem, splitManifest, viewTarget, type ManifestEntry } from "../lib/datasheets";
import type { MessageKey } from "../i18n/keys";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { useRacking } from "./data";
import { DATASHEET_TEXT as T } from "./datasheetText";
import type { Ws } from "./PermitWorkspace";

const ROLE: Record<string, MessageKey> = {
  module: "permit.equipment.kind.module",
  inverter: "permit.documents.role.inverter",
  microinverter: "permit.equipment.kind.microinverter",
  optimizer: "permit.equipment.kind.optimizer",
  rail: "permit.documents.role.rail",
  attachment: "permit.documents.role.attachment",
  rsd: "permit.documents.rsd",
};

export function manifestOf(ws: Ws): ManifestEntry[] {
  return ((ws.preview.data?.inputs as Record<string, unknown> | undefined)?.documents ?? []) as ManifestEntry[];
}

function StateBadge({ e }: { e: ManifestEntry }) {
  const t = useT();
  const s = datasheetState(e);
  if (s.kind === "attached")
    return (
      <Badge tone="ok">
        <FileCheck2 size={12} /> {s.part ? t(T.attachedPart, { first: s.part.first, last: s.part.last, total: s.part.total }) : t(T.attached, { pages: s.pages })}
      </Badge>
    );
  if (s.kind === "fetch")
    return (
      <span title={t(T.willFetchHint)}>
        <Badge tone="info">
          <Clock size={12} /> {t(T.willFetch)}
        </Badge>
      </span>
    );
  if (s.kind === "reference")
    return (
      <span title={t(T.referencedHint)}>
        <Badge>{t(T.referenced)}</Badge>
      </span>
    );
  return (
    <Badge tone="danger">
      <FileWarning size={12} /> {s.kind === "generic" ? t(T.generic) : t(T.missing)}
    </Badge>
  );
}

/** One document: its state, where it comes from, View (a link signed per click), the official page, and the
 * company's own PDF (attach, replace, remove). */
function DocumentRow({ ws, e, title }: { ws: Ws; e: ManifestEntry; title: string }) {
  const t = useT();
  const orgId = useOrgId();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const target = viewTarget(e);
  const own = ownDocumentId(e);
  const refresh = () => qc.invalidateQueries({ queryKey: ["plan-set-preview", ws.designId] });
  const view = useMutation({
    mutationFn: () =>
      unwrap(api.GET("/v1/orgs/{org_id}/permit-datasheets/link", { params: { path: { org_id: orgId }, query: { source: target!.source, id: target!.id } } })),
    onSuccess: (link) => window.open(link.url, "_blank", "noopener"),
    onError: (err) => {
      console.warn("datasheet link", errorMessage(err));
      toast(t(T.viewFailed), true);
    },
  });
  const attach = useMutation({
    mutationFn: async (file: File) => {
      const problem = pdfProblem(file);
      if (problem) throw new Error(t(T[problem]));
      const form = new FormData();
      form.append("file", file);
      form.append("subject_kind", e.subject_kind);
      form.append("subject_key", e.subject_key);
      await rawFetch(`/api/v1/orgs/${orgId}/permit-datasheets`, { method: "POST", body: form });
    },
    onSuccess: () => {
      toast(t(T.attachedToast, { label: e.label }));
      refresh();
    },
    onError: (err) => toast(errorMessage(err), true),
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/orgs/{org_id}/permit-datasheets/{document_id}", { params: { path: { org_id: orgId, document_id: own! } } })),
    onSuccess: () => {
      toast(t(T.removedToast, { label: e.label }));
      refresh();
    },
    onError: (err) => toast(errorMessage(err), true),
  });
  const origin = e.file_id && e.origin ? e.origin : null;
  return (
    <div className="pw-doc-row pw-doc-entry" data-testid={`datasheet-${e.role}-${e.doc_kind}`}>
      <span className="pw-doc-name">
        <span className="muted">{title}</span> · {e.title ?? e.label}
        {origin && (
          <span className="small faint" title={T.originHint[origin] ? t(T.originHint[origin]) : undefined}>
            {" "}
            · {T.origin[origin] ? t(T.origin[origin]) : origin}
          </span>
        )}
      </span>
      <span className="pw-doc-actions">
        <StateBadge e={e} />
        {target && (
          <Button size="sm" variant="ghost" icon={<Eye size={14} />} loading={view.isPending} title={t(T.viewHint)} onClick={() => view.mutate()}>
            {t(T.view)}
          </Button>
        )}
        {e.official_url && (
          <a className="btn btn-sm btn-ghost" href={e.official_url} target="_blank" rel="noopener noreferrer" title={t(T.officialPageHint)}>
            <DirIcon><ExternalLink size={14} /></DirIcon> {t(T.officialPage)}
          </a>
        )}
        {ws.canEdit && canAttachOwn(e) && (
          <>
            {own ? (
              <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} loading={remove.isPending} onClick={() => remove.mutate()}>
                {t(T.remove)}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" icon={<FileUp size={14} />} loading={attach.isPending} onClick={() => input.current?.click()}>
                {e.file_id || e.pending_fetch ? t(T.replace) : t(T.attach)}
              </Button>
            )}
            <input
              ref={input}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              aria-label={t(T.attachFor, { label: e.label })}
              onChange={(ev) => {
                const file = ev.target.files?.[0];
                ev.target.value = "";
                if (file) attach.mutate(file);
              }}
            />
          </>
        )}
      </span>
    </div>
  );
}

/** Equipment step: the datasheets the plan set appends, each with its state and the manufacturer's official page. */
export function DatasheetList({ ws }: { ws: Ws }) {
  const t = useT();
  const { datasheets, supporting } = splitManifest(manifestOf(ws));
  if (!datasheets.length) return null;
  const missing = missingCount(datasheets);
  return (
    <Card title={t(T.title)} description={missing ? t(T.descriptionMissing) : t(T.descriptionAttached)}>
      <div data-anchor="datasheets">
        {datasheets.map((e) => (
          <DocumentRow key={`${e.role}:${e.label}`} ws={ws} e={e} title={ROLE[e.role] ? t(ROLE[e.role]!) : e.role} />
        ))}
      </div>
      {supporting.length > 0 && (
        <div className="pw-doc-supporting">
          <div className="small muted" title={t(T.supportingDescription)}>
            {t(T.supportingTitle)}
          </div>
          {supporting.map((e) => (
            <DocumentRow key={`${e.doc_kind}:${e.title ?? e.label}`} ws={ws} e={e} title={T.supportingKind[e.doc_kind] ? t(T.supportingKind[e.doc_kind]!) : e.doc_kind} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** Equipment step: a module-level rapid-shutdown device for a string inverter without optimizers. */
export function RsdPicker({ ws, inverter, optimizer }: { ws: Ws; inverter: S["PermitEquipment"] | undefined; optimizer: S["PermitEquipment"] | undefined }) {
  const t = useT();
  const racking = useRacking();
  const specs = (inverter?.specs ?? {}) as Record<string, unknown>;
  if (!inverter || inverter.kind !== "inverter" || optimizer || specs.rapid_shutdown === "mlpe" || specs.optimizer_string) return null;
  const options = [{ value: "", label: t("permit.documents.rsdNone") }, ...(racking.data?.rsd_devices ?? []).map((r) => ({ value: r.key, label: r.label }))];
  return (
    <div data-anchor="rsd" className="pw-anchor">
      <Field label={t("permit.documents.rsd")} hint={t("permit.documents.rsdHint")}>
        <Select value={ws.details.rsd_device ?? ""} disabled={!ws.canEdit} onChange={(v) => ws.setDetails((d) => ({ ...d, rsd_device: v || null }))} options={options} ariaLabel={t("permit.documents.rsd")} />
      </Field>
    </div>
  );
}

/** Structure step: the attachment, and the manufacturer's fastener and embedment when the installer has them. */
export function AttachmentFields({ ws }: { ws: Ws }) {
  const t = useT();
  const racking = useRacking();
  const st = ws.details.structural ?? {};
  const off = !ws.canEdit;
  const imperial = ws.units === "imperial";
  const setSt = (patch: Partial<S["PermitStructuralInputs"]>) => ws.setDetails((d) => ({ ...d, structural: { ...(d.structural ?? {}), ...patch } }));
  const items = racking.data?.attachments ?? [];
  const chosen = items.find((a) => a.key === st.attachment);
  const embed = st.attachment_embedment_m;
  return (
    <div className="pw-form-grid" data-anchor="attachment">
      <Field label={t("permit.documents.attachment")} hint={t("permit.documents.attachmentHint")}>
        <Select value={st.attachment ?? ""} disabled={off} onChange={(v) => setSt({ attachment: v || null })} options={[{ value: "", label: t("permit.documents.attachmentFromRoof") }, ...items.map((a) => ({ value: a.key, label: a.label }))]} ariaLabel={t("permit.documents.attachment")} />
      </Field>
      <Field label={t("permit.documents.fastener")} hint={t("permit.documents.fastenerHint")}>
        <TextInput value={st.attachment_fastener ?? ""} disabled={off} maxLength={120} placeholder={chosen?.fastener ?? t("permit.documents.fastenerPlaceholder")} onChange={(e) => setSt({ attachment_fastener: e.target.value || null })} />
      </Field>
      <Field label={t("permit.documents.embedment")} hint={t("permit.documents.embedmentHint")}>
        <NumberInput
          value={embed == null ? null : imperial ? Math.round((embed / 0.0254) * 100) / 100 : Math.round(embed * 1000)}
          disabled={off}
          suffix={imperial ? "in" : "mm"}
          placeholder={chosen?.embedment_m ? String(imperial ? Math.round((chosen.embedment_m / 0.0254) * 100) / 100 : Math.round(chosen.embedment_m * 1000)) : ""}
          onChange={(v) => setSt({ attachment_embedment_m: v == null ? null : imperial ? Math.round(v * 0.0254 * 10000) / 10000 : v / 1000 })}
        />
      </Field>
    </div>
  );
}
